import type { PrimeReactPTOptions } from 'primereact/api'

export const passthrough: PrimeReactPTOptions = {
  button: {
    root: (options) => ({
      className: [
        'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border cursor-pointer transition-all',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        {
          'bg-primary text-white border-transparent hover:bg-primary-dark':
            options?.props?.severity === undefined,
          'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700':
            options?.props?.severity === 'secondary',
          'bg-error text-white border-transparent hover:bg-red-700':
            options?.props?.severity === 'danger',
          'bg-success text-white border-transparent hover:bg-green-700':
            options?.props?.severity === 'success',
          'bg-warning text-white border-transparent hover:bg-yellow-700':
            options?.props?.severity === 'warning',
          'p-2': options?.props?.size === 'small',
          'px-6 py-3 text-base': options?.props?.size === 'large',
        },
      ],
    }),
    label: { className: 'font-medium' },
    icon: { className: 'mr-2' },
  },
  inputtext: {
    root: {
      className: [
        'w-full px-3 py-2 text-sm border rounded-md',
        'bg-white dark:bg-gray-800',
        'text-gray-900 dark:text-gray-100',
        'border-gray-300 dark:border-gray-600',
        'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
        'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      ],
    },
  },
  dropdown: {
    root: {
      className: [
        'inline-flex cursor-pointer relative select-none',
        'bg-white dark:bg-gray-800',
        'border border-gray-300 dark:border-gray-600',
        'rounded-md',
        'transition-colors',
        'hover:border-primary',
      ],
    },
    input: {
      className: [
        'cursor-pointer block flex-auto overflow-hidden overflow-ellipsis whitespace-nowrap',
        'px-3 py-2 text-sm',
        'text-gray-900 dark:text-gray-100',
        'bg-transparent',
      ],
    },
    trigger: {
      className: [
        'flex items-center justify-center shrink-0',
        'text-gray-500 dark:text-gray-400',
        'w-10',
      ],
    },
    panel: {
      className: [
        'bg-white dark:bg-gray-800',
        'border border-gray-300 dark:border-gray-600',
        'rounded-md shadow-lg',
        'mt-1',
      ],
    },
    list: { className: 'py-1 list-none m-0' },
    item: (options) => ({
      className: [
        'cursor-pointer font-normal overflow-hidden relative whitespace-nowrap',
        'px-3 py-2 text-sm',
        'text-gray-900 dark:text-gray-100',
        'transition-colors',
        {
          'bg-primary text-white': options?.context?.selected,
          'hover:bg-gray-100 dark:hover:bg-gray-700': !options?.context?.selected,
        },
      ],
    }),
  },
  datatable: {
    root: { className: 'relative' },
    table: { className: 'w-full border-collapse text-sm' },
    thead: { className: 'bg-gray-50 dark:bg-gray-800' },
    headerRow: { className: '' },
    column: {
      headerCell: {
        className: [
          'px-3 py-3 text-left font-semibold',
          'text-gray-600 dark:text-gray-300',
          'border-b border-gray-200 dark:border-gray-700',
        ],
      },
      bodyCell: {
        className: [
          'px-3 py-3',
          'text-gray-900 dark:text-gray-100',
          'border-b border-gray-200 dark:border-gray-700',
        ],
      },
    },
    tbody: { className: '' },
    bodyRow: {
      className: ['hover:bg-gray-50 dark:hover:bg-gray-800', 'transition-colors'],
    },
  },
  card: {
    root: {
      className: [
        'bg-white dark:bg-gray-800',
        'rounded-lg shadow',
        'border border-gray-200 dark:border-gray-700',
      ],
    },
    body: { className: 'p-6' },
    title: {
      className: ['text-lg font-semibold', 'text-gray-900 dark:text-gray-100', 'mb-4'],
    },
    content: { className: 'text-gray-700 dark:text-gray-300' },
  },
  checkbox: {
    root: { className: 'inline-flex cursor-pointer select-none items-center' },
    box: (options) => ({
      className: [
        'flex items-center justify-center',
        'w-5 h-5 rounded',
        'border-2 transition-colors',
        {
          'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800':
            !options?.props?.checked,
          'border-primary bg-primary': options?.props?.checked,
        },
      ],
    }),
    icon: { className: 'w-3 h-3 text-white' },
  },
  tag: {
    root: (options) => ({
      className: [
        'inline-flex items-center justify-center',
        'px-2 py-0.5 text-xs font-medium rounded-full',
        {
          'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200':
            options?.props?.severity === undefined || options?.props?.severity === 'secondary',
          'bg-primary/10 text-primary': options?.props?.severity === 'info',
          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':
            options?.props?.severity === 'success',
          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400':
            options?.props?.severity === 'warning',
          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400':
            options?.props?.severity === 'danger',
        },
      ],
    }),
  },
}
